import { ArrowLeft, ArrowRight } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { Table as TableType } from "@tanstack/react-table";

import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto rounded-2xl border border-border bg-card p-1">
    <table
      ref={ref}
      className={cn("w-full table-fixed caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b-0", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      "divide-y divide-border overflow-hidden rounded-xl bg-background ring-1 ring-border [&_tr:last-child]:border-0",
      "[&_tr:last-child_td:first-child]:rounded-bl-xl [&_tr:last-child_td:last-child]:rounded-br-xl", // rounding the last row corners
      "[&_tr:first-child_td:first-child]:rounded-tl-xl [&_tr:first-child_td:last-child]:rounded-tr-xl", // rounding the first row corners
      className,
    )}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("[&>tr]:last:border-b-0", className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "rounded-xl transition-colors hover:bg-card data-[state=selected]:bg-card",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-11 px-4 text-left align-middle text-xs font-medium text-muted [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "h-16 p-4 align-middle dark:text-secondary [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

const TablePagination = <TData,>({
  className,
  table,
  isLoading,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  table: TableType<TData>;
  isLoading?: boolean;
}) => {
  const [clickedButton, setClickedButton] = useState<"prev" | "next" | null>(
    null,
  );

  // Reset clicked button when loading finishes
  useEffect(() => {
    if (!isLoading) {
      setClickedButton(null);
    }
  }, [isLoading]);

  return (
    <tr {...props}>
      <td colSpan={table.getAllColumns().length}>
        <div
          className={cn(
            "ml-auto flex items-center justify-between gap-2 px-3 pb-1 pt-2",
            className,
          )}
        >
          <div>
            <Select
              value={table.getState().pagination.pageSize.toString()}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Show 10" />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={pageSize.toString()}>
                    Show {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <p className="mr-2 text-sm text-secondary dark:text-secondary">
              Page{" "}
              <span className="font-medium">
                {table.getState().pagination.pageIndex + 1}
              </span>{" "}
              of
              <span className="font-medium"> {table.getPageCount()}</span>
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setClickedButton("prev");
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
                isLoading={isLoading && clickedButton === "prev"}
                isAnimated
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setClickedButton("next");
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
                isLoading={isLoading && clickedButton === "next"}
                isAnimated
              >
                <span className="hidden sm:inline">Next</span>
                <ArrowRight className="h-4 w-4 sm:ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};
TablePagination.displayName = "TablePagination";

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
};
